<template>
    <nav v-if="breadcrumbs.length" class="breadcrumbs-component" aria-label="Breadcrumb">
        <ol class="breadcrumbs-component__list">
            <li v-for="(breadcrumb, index) in breadcrumbs" :key="breadcrumb.key" class="breadcrumbs-component__item">
                <span v-if="index !== breadcrumbs.length - 1 && breadcrumb.to" :to="breadcrumb.to"
                    class="breadcrumbs-component__link">
                    {{ breadcrumb.label }}
                </span>
                <span v-else class="breadcrumbs-component__current"
                    :aria-current="index === breadcrumbs.length - 1 ? 'page' : null">
                    {{ breadcrumb.label }}
                </span>
            </li>
        </ol>
    </nav>
</template>

<script>
const HOME_ROUTE_NAME = "home";
const EXCLUDED_ROUTE_NAMES = ["Panel"];

const ROUTE_TRANSLATION_KEYS = {
    "client-settings": "com.file.contact-box.settings",
    "client-settings-ui": "com.file.settings.menu.ui-settings",
    "client-settings-integration": "com.file.settings.menu.integration",
    "client-settings-custom-css": "com.file.settings.menu.custom-css",
    "client-settings-room-map": "com.file.settings.menu.room-map",
    "client-settings-placeholder": "com.file.settings.menu.placeholder",
    "client-settings-invoice-data": "com.file.settings.menu.invoice-data",
    "client-settings-discussion-profile": "com.file.settings.menu.discussion-profile",
    "client-settings-voting": "com.file.settings.menu.voting"
};

export default {
    name: "BreadcrumbsComponent",
    computed: {
        breadcrumbs() {
            const parentBreadcrumbs = this.getParentBreadcrumbs();
            const matchedBreadcrumbs = this.$route.matched
                .filter((routeRecord) => routeRecord.name && !EXCLUDED_ROUTE_NAMES.includes(String(routeRecord.name)))
                .map((routeRecord) => this.createBreadcrumb(routeRecord))
                .filter((breadcrumb) => breadcrumb.routeName !== HOME_ROUTE_NAME)
                .filter((breadcrumb) => breadcrumb.label);

            const breadcrumbs = [
                {
                    routeName: HOME_ROUTE_NAME,
                    key: "home-root",
                    label: this.humanizeValue(HOME_ROUTE_NAME),
                    to: { name: HOME_ROUTE_NAME }
                },
                ...parentBreadcrumbs,
                ...matchedBreadcrumbs
            ];

            return breadcrumbs.filter((breadcrumb, index, items) => {
                return items.findIndex((item) => item.routeName === breadcrumb.routeName) === index;
            });
        }
    },
    methods: {
        createBreadcrumb(routeRecord) {
            return {
                routeName: String(routeRecord.name),
                key: `${routeRecord.name}-${routeRecord.path}`,
                label: this.getBreadcrumbLabel(routeRecord),
                to: this.getBreadcrumbTarget(routeRecord)
            };
        },
        getParentBreadcrumbs() {
            const parentRouteNames = this.$route.meta?.parentBreadcrumbs;

            if (!Array.isArray(parentRouteNames)) {
                return [];
            }

            return parentRouteNames
                .map((routeName) => this.createBreadcrumbFromRouteName(routeName))
                .filter(Boolean);
        },
        createBreadcrumbFromRouteName(routeName) {
            const resolvedRoute = this.$router.resolve({
                name: routeName,
                params: this.$route.params
            });
            const routeRecord = resolvedRoute.matched[resolvedRoute.matched.length - 1];

            if (!routeRecord?.name) {
                return null;
            }

            return this.createBreadcrumb(routeRecord);
        },
        getBreadcrumbLabel(routeRecord) {
            const routeName = routeRecord.name ? String(routeRecord.name) : "";
            const metaBreadcrumb = routeRecord.meta?.breadcrumb;

            if (typeof metaBreadcrumb === "function") {
                return metaBreadcrumb(this.$route, this);
            }

            if (typeof metaBreadcrumb === "string" && metaBreadcrumb.length) {
                return metaBreadcrumb;
            }

            if (ROUTE_TRANSLATION_KEYS[routeName]) {
                return this.$t(ROUTE_TRANSLATION_KEYS[routeName]);
            }

            if (routeName.length) {
                return this.humanizeValue(routeName);
            }

            return this.humanizeValue(routeRecord.path);
        },
        getBreadcrumbTarget(routeRecord) {
            if (!routeRecord.name) {
                return null;
            }

            return {
                name: routeRecord.name,
                params: this.$route.params
            };
        },
        humanizeValue(value) {
            return String(value)
                .split("/")
                .filter(Boolean)
                .pop()
                .replace(/[-_]/g, " ")
                .replace(/\b\w/g, (char) => char.toUpperCase());
        }
    }
}
</script>

<style lang="scss" scoped>
.breadcrumbs-component {
    padding: 0 48px;

    &__list {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 8px;
        margin: 0;
        padding: 0;
        list-style: none;
    }

    &__item {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        color: #808396;
        font-size: 14px;
        line-height: 1.4;

        &:not(:last-child)::after {
            content: "/";
            color: #bbc2ce;
        }
    }

    &__link {
        color: #808396;
        text-decoration: none;
    }

    &__current {
        color: #00082c;
        font-weight: 600;
    }
}
</style>
